import { Editor } from '@tiptap/react'
import { Icon } from '@yes.technology/react-toolkit'
import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { isAllowedLinkHref } from './htmlEditorUtils'
import {
  ColorInput,
  LinkInputGroup,
  LinkUrlInput,
  Toolbar,
  ToolbarButton,
  ToolbarGroup
} from './HtmlEditor.styles'

type HtmlEditorToolbarProps = {
  editor: Editor
}

const HtmlEditorToolbar = ({ editor }: HtmlEditorToolbarProps) => {
  const { t } = useTranslation()
  const [isLinkEditorOpen, setIsLinkEditorOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('https://')
  const [linkError, setLinkError] = useState(false)

  const openLinkEditor = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined
    setLinkUrl(previousUrl || 'https://')
    setLinkError(false)
    setIsLinkEditorOpen(true)
  }

  const closeLinkEditor = () => {
    setIsLinkEditorOpen(false)
    setLinkError(false)
  }

  const applyLink = (event: FormEvent) => {
    event.preventDefault()

    const trimmedUrl = linkUrl.trim()

    if (!trimmedUrl) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      closeLinkEditor()
      return
    }

    if (!isAllowedLinkHref(trimmedUrl)) {
      setLinkError(true)
      return
    }

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: trimmedUrl })
      .run()
    closeLinkEditor()
  }

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    closeLinkEditor()
  }

  return (
    <Toolbar>
      <ToolbarGroup>
        <ToolbarButton
          type='button'
          $active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title='Bold'
        >
          B
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title='Italic'
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title='Underline'
        >
          <u>U</u>
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title='Strikethrough'
        >
          <s>S</s>
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <ToolbarButton
          type='button'
          $active={editor.isActive('paragraph')}
          onClick={() => editor.chain().focus().setParagraph().run()}
          title='Paragraph'
        >
          P
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive('heading', { level: 1 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          title='Heading 1'
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive('heading', { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          title='Heading 2'
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive('heading', { level: 3 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          title='Heading 3'
        >
          H3
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <ToolbarButton
          type='button'
          $active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title='Bullet list'
        >
          <Icon
            iconName='BulletedList'
            icSize='semiSmall'
            nonFocusable
            removeOutlineAndBorder
          />
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title='Ordered list'
        >
          1.
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <ToolbarButton
          type='button'
          $active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title='Align left'
        >
          <Icon
            iconName='LeftAlignment'
            icSize='semiSmall'
            nonFocusable
            removeOutlineAndBorder
          />
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title='Align center'
        >
          <Icon
            iconName='CentralizedAlignment'
            icSize='semiSmall'
            nonFocusable
            removeOutlineAndBorder
          />
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title='Align right'
        >
          <Icon
            iconName='RightAlignment'
            icSize='semiSmall'
            nonFocusable
            removeOutlineAndBorder
          />
        </ToolbarButton>
        <ToolbarButton
          type='button'
          $active={editor.isActive({ textAlign: 'justify' })}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          title='Justify'
        >
          <Icon
            iconName='JustifiedAlignment'
            icSize='semiSmall'
            nonFocusable
            removeOutlineAndBorder
          />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <ToolbarButton
          type='button'
          $active={editor.isActive('link') || isLinkEditorOpen}
          onClick={openLinkEditor}
          title='Link'
        >
          <Icon
            iconName='Links'
            icSize='semiSmall'
            nonFocusable
            removeOutlineAndBorder
          />
        </ToolbarButton>
        <ColorInput
          type='color'
          title='Text color'
          aria-label={t('object-view.html-editor.text-color')}
          onChange={(event) =>
            editor.chain().focus().setColor(event.target.value).run()
          }
        />
        <ToolbarButton
          type='button'
          onClick={() => editor.chain().focus().unsetColor().run()}
          title='Remove color'
        >
          <Icon
            iconName='Rubber'
            icSize='semiSmall'
            nonFocusable
            removeOutlineAndBorder
          />
        </ToolbarButton>
      </ToolbarGroup>

      {isLinkEditorOpen && (
        <LinkInputGroup>
          <form onSubmit={applyLink}>
            <LinkUrlInput
              type='url'
              value={linkUrl}
              aria-label={t('object-view.html-editor.link-url')}
              aria-invalid={linkError}
              placeholder={t('object-view.html-editor.link-url')}
              onChange={(event) => {
                setLinkUrl(event.target.value)
                setLinkError(false)
              }}
            />
            <ToolbarButton
              type='submit'
              title={t('object-view.html-editor.apply-link')}
            >
              {t('object-view.html-editor.apply-link')}
            </ToolbarButton>
            <ToolbarButton
              type='button'
              title={t('object-view.html-editor.remove-link')}
              onClick={removeLink}
            >
              {t('object-view.html-editor.remove-link')}
            </ToolbarButton>
            <ToolbarButton
              type='button'
              title={t('data-tables.cancel')}
              onClick={closeLinkEditor}
            >
              {t('data-tables.cancel')}
            </ToolbarButton>
          </form>
        </LinkInputGroup>
      )}
    </Toolbar>
  )
}

export default HtmlEditorToolbar
